import React from 'react';
import { Link } from 'react-router-dom';

const ArtrixPrivacyPolicy: React.FC = () => {
  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: '2rem' }}>
          <Link 
            to="/" 
            style={{ 
              color: 'var(--accent-color)', 
              textDecoration: 'none',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← Back to Home
          </Link>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: '700',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Artrix - Dall E 3 Image AI Privacy Policy
          </h1>
          <div className="section-divider"></div>
        </div>

        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          lineHeight: '1.6',
          fontSize: '1rem'
        }}>
          <p style={{ marginBottom: '2rem', fontSize: '1rem' }}>
            <strong>Effective Date:</strong> October 10, 2025<br/>
            <strong>Last Updated:</strong> October 10, 2025
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>Introduction</h2>
          
          <p style={{ marginBottom: '1rem' }}>
            Welcome to Artrix! We are committed to protecting your privacy and ensuring transparency about how we handle your data. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your personal data.
          </p>
          
          <p style={{ marginBottom: '2rem' }}>
            By using Artrix, you agree to the collection and use of information in accordance with this policy.
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>1. Information We Collect</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>1.1 Information You Provide</h3>
          
          <p style={{ marginBottom: '0.5rem' }}><strong>User Profile</strong></p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Username (optional, for public gallery features)</li>
            <li>Profile picture (optional, if you choose to upload one)</li>
            <li>Artworks you choose to share publicly</li>
          </ul>

          <p style={{ marginBottom: '0.5rem' }}><strong>Generated Content</strong></p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>AI-generated images you create</li>
            <li>Prompts and art style preferences</li>
            <li>Metadata about your creations (timestamps, settings used)</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>1.2 Automatically Collected Information</h3>
          
          <p style={{ marginBottom: '0.5rem' }}><strong>Device Information</strong></p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Device type and model</li>
            <li>Operating system version</li>
            <li>App version</li>
            <li>Unique device identifier (stored securely in iOS Keychain)</li>
          </ul>

          <p style={{ marginBottom: '0.5rem' }}><strong>Usage Data</strong></p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>App features you use</li>
            <li>Number of images generated</li>
            <li>Subscription status</li>
            <li>Crash logs and error reports</li>
          </ul>

          <p style={{ marginBottom: '0.5rem' }}><strong>We DO NOT Collect:</strong></p>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li>Your name, email, or phone number (unless you contact us)</li>
            <li>Your location or GPS data</li>
            <li>Your contacts or photos outside the app</li>
            <li>Browsing history</li>
            <li>Sensitive personal information</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>2. How We Use Your Information</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.1 Core Functionality</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Image Generation</strong>: Processing your prompts with OpenAI's DALL-E 3 API</li>
            <li><strong>Gallery Management</strong>: Storing and displaying your created artworks</li>
            <li><strong>Public Sharing</strong>: Enabling you to share artworks with the community</li>
            <li><strong>User Profiles</strong>: Allowing you to create and maintain a public profile</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.2 Service Improvement</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>App Performance</strong>: Monitoring crashes and errors to improve stability</li>
            <li><strong>Feature Development</strong>: Understanding which features are most popular</li>
            <li><strong>User Experience</strong>: Optimizing the app based on usage patterns</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.3 Subscription Management</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Payment Processing</strong>: Managing in-app purchases through Apple and RevenueCat</li>
            <li><strong>Access Control</strong>: Determining premium feature access</li>
            <li><strong>Subscription Status</strong>: Tracking active subscriptions and renewals</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.4 Security & Fraud Prevention</h3>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>App Integrity</strong>: Using Firebase App Check to prevent abuse</li>
            <li><strong>API Protection</strong>: Securing our AI generation service</li>
            <li><strong>Rate Limiting</strong>: Preventing excessive API usage</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>3. Data Storage and Security</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>3.1 Where Your Data is Stored</h3>
          
          <p style={{ marginBottom: '0.5rem' }}><strong>Local Storage (Your Device)</strong></p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Generated artworks (stored in app's Documents directory)</li>
            <li>User preferences and settings</li>
            <li>Unique device identifier (iOS Keychain)</li>
          </ul>

          <p style={{ marginBottom: '0.5rem' }}><strong>Cloud Storage (Firebase)</strong></p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Public artworks you choose to share</li>
            <li>User profile information (username, profile picture)</li>
            <li>Artwork metadata (prompts, styles, likes)</li>
          </ul>

          <p style={{ marginBottom: '0.5rem' }}><strong>Third-Party Services</strong></p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>RevenueCat: Subscription and purchase information</li>
            <li>OpenAI: Prompts sent for image generation (not stored by us)</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>3.2 Data Security Measures</h3>
          <p style={{ marginBottom: '0.5rem' }}>We implement industry-standard security measures:</p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Encryption</strong>: All data transmitted over secure HTTPS connections</li>
            <li><strong>Keychain</strong>: Sensitive identifiers stored in iOS Keychain (encrypted)</li>
            <li><strong>Firebase Security</strong>: Protected by Firebase security rules</li>
            <li><strong>App Check</strong>: Prevents unauthorized API access</li>
            <li><strong>No Passwords</strong>: We don't store passwords (no traditional authentication)</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>3.3 Data Retention</h3>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>Local Artworks</strong>: Stored until you delete the app</li>
            <li><strong>Public Artworks</strong>: Stored indefinitely (unless you request deletion)</li>
            <li><strong>User Profiles</strong>: Retained while you use the app</li>
            <li><strong>Usage Analytics</strong>: Aggregated and anonymized after 90 days</li>
            <li><strong>Deleted Content</strong>: Permanently removed from our servers within 30 days</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>4. Third-Party Services</h2>
          <p style={{ marginBottom: '1rem' }}>Artrix uses the following third-party services:</p>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>4.1 OpenAI (DALL-E 3)</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Purpose</strong>: AI image generation</li>
            <li><strong>Data Shared</strong>: Your text prompts and selected art styles</li>
            <li><strong>Privacy Policy</strong>: <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>OpenAI Privacy Policy</a></li>
            <li><strong>Data Retention</strong>: OpenAI may store prompts for 30 days per their policy</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>4.2 Firebase (Google)</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Purpose</strong>: Cloud storage, database, and app analytics</li>
            <li><strong>Data Shared</strong>: Public artworks, user profiles, app usage data</li>
            <li><strong>Privacy Policy</strong>: <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>Firebase Privacy Policy</a></li>
            <li><strong>Location</strong>: Google Cloud Platform servers</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>4.3 RevenueCat</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Purpose</strong>: Subscription and in-app purchase management</li>
            <li><strong>Data Shared</strong>: Purchase history, subscription status, anonymous user ID</li>
            <li><strong>Privacy Policy</strong>: <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>RevenueCat Privacy Policy</a></li>
            <li><strong>Data Retention</strong>: As long as you have an active subscription</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>4.4 Apple App Store</h3>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>Purpose</strong>: Payment processing and app distribution</li>
            <li><strong>Data Shared</strong>: Purchase information, device information</li>
            <li><strong>Privacy Policy</strong>: <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>Apple Privacy Policy</a></li>
            <li><strong>Payment Data</strong>: Handled entirely by Apple (we never see your payment info)</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>5. Data Sharing and Disclosure</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>5.1 Public Gallery Feature</h3>
          <p style={{ marginBottom: '0.5rem' }}>When you choose to share an artwork to the public gallery:</p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>The artwork image becomes publicly visible</li>
            <li>Your username (if set) is displayed with the artwork</li>
            <li>The artwork's prompt and settings are visible</li>
            <li>Other users can view and like your artwork</li>
          </ul>
          <p style={{ marginBottom: '1rem' }}>You can control what you share and can unshare artworks at any time.</p>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>5.2 We DO NOT Sell Your Data</h3>
          <p style={{ marginBottom: '0.5rem' }}>We do not and will never:</p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Sell your personal information to third parties</li>
            <li>Share your data with advertisers</li>
            <li>Rent or trade your information</li>
            <li>Use your data for marketing outside the app</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>5.3 Legal Requirements</h3>
          <p style={{ marginBottom: '0.5rem' }}>We may disclose your information if required by law:</p>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li>Court orders or legal processes</li>
            <li>Protecting our rights or property</li>
            <li>Investigating potential violations of our Terms of Service</li>
            <li>Ensuring user safety</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>6. Your Privacy Rights</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>6.1 Access and Control</h3>
          <p style={{ marginBottom: '0.5rem' }}>You have the right to:</p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Access</strong>: View all your stored data within the app</li>
            <li><strong>Edit</strong>: Modify your username and profile picture</li>
            <li><strong>Delete</strong>: Remove your artworks and account data</li>
            <li><strong>Export</strong>: Request a copy of your data (contact us)</li>
            <li><strong>Unshare</strong>: Remove artworks from public gallery</li>
            <li><strong>Opt-Out</strong>: Disable analytics (limited due to technical requirements)</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>6.2 How to Exercise Your Rights</h3>
          <p style={{ marginBottom: '0.5rem' }}><strong>Within the App:</strong></p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>Delete artworks: Gallery → Long press → Delete</li>
            <li>Unshare artworks: Gallery → Toggle share switch</li>
            <li>Delete profile: Settings → Profile → Delete Account</li>
          </ul>

          <p style={{ marginBottom: '0.5rem' }}><strong>Contact Us:</strong></p>
          <p style={{ marginBottom: '2rem' }}>For data export, account deletion, or privacy questions, contact us at info@rapidnova.co. We will respond within 30 days.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>7. Children's Privacy</h2>
          <p style={{ marginBottom: '1rem' }}>Artrix is intended for users aged 13 and older.</p>
          
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>7.1 Age Restrictions</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li>We do not knowingly collect data from children under 13</li>
            <li>We do not target children with our marketing</li>
            <li>Age-appropriate content moderation is in place</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>7.2 Parental Rights</h3>
          <p style={{ marginBottom: '0.5rem' }}>If you believe your child under 13 has provided us with personal information:</p>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li>Contact us immediately at info@rapidnova.co</li>
            <li>We will delete the information within 72 hours</li>
            <li>We will terminate the account</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>8. International Data Transfers</h2>
          <p style={{ marginBottom: '1rem' }}>Your data may be stored and processed in the United States (Firebase, OpenAI, RevenueCat servers) and your region (Apple servers). We ensure adequate protection through Standard Contractual Clauses, Privacy Shield frameworks, Data Processing Agreements with vendors, and encryption during transit and at rest.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>9. Updates to This Policy</h2>
          <p style={{ marginBottom: '0.5rem' }}>We may update this Privacy Policy to reflect changes in our practices, legal requirements, or new features. When we make significant changes:</p>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li>Update the "Last Updated" date at the top</li>
            <li>Display an in-app notification</li>
            <li>Email notification (if we have your email)</li>
            <li>30-day notice for material changes</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>10. Contact Information</h2>
          <p style={{ marginBottom: '1rem' }}>For privacy-related questions or requests, please contact us:</p>
          
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>Email</h3>
          <p style={{ marginBottom: '1.5rem' }}>info@rapidnova.co</p>
          
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>Address</h3>
          <p style={{ marginBottom: '2rem' }}>
            60 Paya Lebar Road, #07-54<br/>
            Paya Lebar Square<br/>
            Singapore 409051
          </p>

          <hr style={{ margin: '3rem 0', border: 'none', borderTop: '1px solid #eee' }} />

          <p style={{ marginBottom: '1rem', fontStyle: 'italic' }}>
            <strong>Thank you for trusting Artrix with your creative journey!</strong>
          </p>

          <p style={{ marginBottom: '1rem', fontStyle: 'italic' }}>
            If you have any questions about this Privacy Policy, please don't hesitate to contact us at info@rapidnova.co.
          </p>

          <p style={{ marginBottom: '0', fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
            This privacy policy was created to comply with: California Consumer Privacy Act (CCPA), General Data Protection Regulation (GDPR), Children's Online Privacy Protection Act (COPPA), and Apple App Store Guidelines.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ArtrixPrivacyPolicy;
